import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { GetApiData, PostApiData } from "../../api/general";
import { ConfirmSenderDetailsRequest, RRAuthResponse, SenderDetailsResponse } from "../../types/api/readyremit";
import { ServiceProviderFieldType } from "../../types/api/general";
import { ROUTES } from "../../constants/api";
import { ROUTES as PAGE_ROUTES } from "../../constants/clientRoutes";
import { withDynamicForm } from "../../HOCs/withDynamicForm";
import { useDispatch } from "react-redux";
import { SetRRAuth } from "../../actions/readyRemit";
import i18n from "../../i18n/config";
import { EVENTS } from "../../constants/events";
import { trackEvent } from "../../services/eventTracker";
import { MoneyTransferPage } from "../../types/moneyTransfer";
import { SubmissionError } from "redux-form";

import FlowScreen from "../../components/Layouts/FlowScreen";
import useErrorModal from "../../hooks/useErrorModal";
import DynamicFormWrapper from "../Common/DynamicFormWrapper";
import PageBlock from "../../components/Layouts/PageBlock";
import InputSkeletonGroup from "../../components/Common/Loaders/InputSkeleton";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import { RR_RSP, RR_TRANSFER_METHODS } from "../../constants/enums";

const SenderDetails = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();
  const [result, setResult] = useState<SenderDetailsResponse | null>(null);
  const [transferData, setTransferData] = useState({
    rsp: "",
    transferMethod: ""
  });
  const eventTypes = {
    back: "back",
    continue: "continue"
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const rsp = queryParams.get("rsp");
    const transferMethod = queryParams.get("transferMethod");
    setTransferData({ rsp: rsp ?? "", transferMethod: transferMethod ?? "" });

    getSenderDetails(rsp ?? "", transferMethod ?? "");
  }, []);

  const getSenderDetails = async (rsp: string, transferMethod: string) => {
    // Placeholder for API call to get sender details
    const url = ROUTES.READY_REMIT.GET_SENDER_DETAILS(rsp, transferMethod);
    try {
      const response = await GetApiData<SenderDetailsResponse>(url);
      setResult(response);
    } catch (err) {
      openErrorModal(err);
    }
  };

  /**
   * Receiver bank account fields come to us in a nested object.
   * This function flattens the object to a single level to send to the api.
   * E.g. { receiver: bank_account: { account_number: 1234 }} becomes { receiver.bank_account.account_number: 1234 }
   */
  const flattenObject = (ob: any, phoneFieldName?: string) => {
    const toReturn: { [key: string]: any } = {};

    for (let i in ob) {
      if (i === phoneFieldName) {
        toReturn[i] = ob[i];
        continue;
      }

      if (!ob.hasOwnProperty(i)) continue;

      if ((typeof ob[i]) == 'object' && ob[i] !== null) {
        let flatObject = flattenObject(ob[i], phoneFieldName);
        for (let x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) continue;

          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
    return toReturn;
  }

  /**
     * Gets errors from the workflow object based on the category passed in.
     * This is used to trigger validation on the sender/receiver pages.
     * */
  const getErrorFromWorkflow = (page: MoneyTransferPage) => {
    const errorObject: Record<string, string> = {};
    if (page) {
      for (let section of page.sections) {
        for (let field of section.fields) {
          if (
            field.validationError &&
            field.validationError.length > 0
          ) {
            errorObject[field.serviceProviderFieldId] =
              field.validationError[0].errorMessage;
          }
        }
      }
    }

    return errorObject;
  }

  /**
   * Takes the values from the form and flattens them to a single level to send to the api.
   */
  const formatValues = (values: { [key: string]: any }) => {
    const flattenedValues = flattenObject(values, "PHONE_NUMBER");
    let fields: ServiceProviderFieldType[] = [];

    if (values != undefined) {
      fields = Object.entries(flattenedValues).map(
        ([serviceProviderFieldId, value]) => ({ serviceProviderFieldId, value })
      );
    }
    return fields;
  }

  const handleSubmit = async (values: { [key: string]: any }) => {
    trackEvent(getEventName(eventTypes.continue));
    const formattedValues = formatValues(values);

    try {
      const url = ROUTES.READY_REMIT.SUBMIT_SENDER_DETAILS;
      const response = await PostApiData<ConfirmSenderDetailsRequest, RRAuthResponse>(url, {
        rsp: transferData.rsp,
        transferMethod: transferData.transferMethod,
        fields: formattedValues
      });
      dispatch(SetRRAuth({
        rsp: response.result.rsp,
        transferMethod: response.result.transferMethod,
        encryptedAuthToken: response.result.encryptedAuthToken,
        senderId: response.result.senderId,
        balanceCheck: response.result.balanceCheck
      }));
      history.push(`${PAGE_ROUTES.RR_SDK.SDK_CONTAINER_BASE}/${response.result.rsp}/${response.result.transferMethod}`);
    } catch (err) {
      const page = (err as { data?: { result?: { page?: MoneyTransferPage } } })?.data?.result?.page;
      if (page) {
        throw new SubmissionError({ ...getErrorFromWorkflow(page) });
      }
      openErrorModal(err);
    }
  }

  const handleBackClick = () => {
    trackEvent(getEventName(eventTypes.back));
    history.goBack();
  }

  const getEventName = (type: string) => {
    const { rsp, transferMethod } = transferData;
    switch (type) {
      case eventTypes.back:
        if (rsp === RR_RSP.MASTERCARD && transferMethod === RR_TRANSFER_METHODS.SEND_WITH_BRIGHTWELL) {
          return EVENTS.READY_REMIT.MC_TO_ACCOUNT.SENDER_DETAILS.BACK_ARROW;
        } else if (rsp === RR_RSP.MASTERCARD && transferMethod === RR_TRANSFER_METHODS.MOBILE_WALLET) {
          return EVENTS.READY_REMIT.MOBILE_WALLET.SENDER_DETAILS.BACK_ARROW;
        } else if (rsp === RR_RSP.VISA && transferMethod === RR_TRANSFER_METHODS.PUSH_TO_CARD) {
          return EVENTS.READY_REMIT.PUSH_TO_CARD.SENDER_DETAILS.BACK_ARROW;
        }
        break;
      case eventTypes.continue:
        if (rsp === RR_RSP.MASTERCARD && transferMethod === RR_TRANSFER_METHODS.SEND_WITH_BRIGHTWELL) {
          return EVENTS.READY_REMIT.MC_TO_ACCOUNT.SENDER_DETAILS.CONTINUE;
        } else if (rsp === RR_RSP.MASTERCARD && transferMethod === RR_TRANSFER_METHODS.MOBILE_WALLET) {
          return EVENTS.READY_REMIT.MOBILE_WALLET.SENDER_DETAILS.CONTINUE;
        } else if (rsp === RR_RSP.VISA && transferMethod === RR_TRANSFER_METHODS.PUSH_TO_CARD) {
          return EVENTS.READY_REMIT.PUSH_TO_CARD.SENDER_DETAILS.CONTINUE;
        }
        break;
      default:
        return "";
    }

    return "";
  }

  const DynamicFormWrapperComponent = withDynamicForm("rr-sender-details", DynamicFormWrapper);

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.READY_REMIT.SENDER_DETAILS.TITLE")}
      className="v3"
    >
      <PageBlock>

        <PageBlock.Loader loadingContent={result === null}>
          <InputSkeletonGroup numInputs={4} />
        </PageBlock.Loader>

        <PageBlock.Body loadingContent={result === null}>
          <h3>{i18n.t("PAGES.READY_REMIT.SENDER_DETAILS.HEADING")}</h3>
          <p className="marengo-grey">{i18n.t("PAGES.READY_REMIT.SENDER_DETAILS.INSTRUCTIONS")}</p>
          {result?.result?.page &&
            <DynamicFormWrapperComponent
              pageData={result.result.page}
              onBack={handleBackClick}
              formName="rr-sender-details"
              onSubmit={handleSubmit}
            />
          }
        </PageBlock.Body>
      </PageBlock>

      <ApiErrorModal />
    </FlowScreen>
  );
}

export default SenderDetails;